import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from "recharts";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import QuickRechargeButton from "@/components/QuickRechargeButton";
import { api, formatEUR, formatNum, formatApiError, WHATSAPP_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    Sparkle, WhatsappLogo, Printer, ArrowLeft, Warning, CheckCircle, Info, Lightbulb, Target, LinkSimple, Bank
} from "@phosphor-icons/react";

export default function AnalysisReport() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user, refresh } = useAuth();
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [interpreting, setInterpreting] = useState(false);
    const [error, setError] = useState("");
    const [errorStatus, setErrorStatus] = useState(null);

    async function load() {
        try {
            const { data } = await api.get(`/analyses/${id}`);
            setAnalysis(data);
        } catch (e) {
            setError(formatApiError(e, "No se encontró el análisis"));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, [id]);

    async function runInterpretation(confirmOverage = false) {
        setError(""); setErrorStatus(null); setInterpreting(true);
        try {
            const { data } = await api.post(`/analyses/${id}/interpret`, { confirm_overage: confirmOverage });
            setAnalysis((a) => ({ ...a, interpretation: data.interpretation }));
            refresh();
        } catch (e) {
            setErrorStatus(e?.response?.status || null);
            setError(formatApiError(e, "Error al generar interpretación"));
        } finally {
            setInterpreting(false);
        }
    }

    const [showMasterModal, setShowMasterModal] = useState(false);
    const [masterData, setMasterData] = useState({
        ebit: "", amortizaciones: "", depreciaciones: "",
        gastos_financieros: "", costo_ventas: "",
        clientes: "", proveedores: "",
    });
    const [runningMaster, setRunningMaster] = useState(false);

    async function runMasterReport() {
        setError(""); setRunningMaster(true);
        try {
            const payload = Object.fromEntries(
                Object.entries(masterData).map(([k, v]) => [k, Number(v) || 0])
            );
            const { data } = await api.post(`/analyses/${id}/interpret-master`, payload);

            // Respuesta antigua sincronía (por retrocompatibilidad)
            if (data.master_report) {
                setAnalysis((a) => ({
                    ...a,
                    master_report: data.master_report,
                    master_ratios: data.master_ratios,
                }));
                setShowMasterModal(false);
                setRunningMaster(false);
                refresh();
                return;
            }

            // Modo asíncrono: hacemos polling suave y si a los 3 min no está,
            // recargamos la página completa (el master queda en el análisis).
            if (data.queued && data.job_id) {
                const jobId = data.job_id;
                const started = Date.now();
                const maxWaitMs = 3 * 60 * 1000;
                let finished = false;
                while (!finished && (Date.now() - started) < maxWaitMs) {
                    await new Promise(r => setTimeout(r, 4000));
                    try {
                        const { data: job } = await api.get(`/master-jobs/${jobId}`);
                        if (job.status === "done" && job.master_report) {
                            setAnalysis((a) => ({
                                ...a,
                                master_report: job.master_report,
                                master_ratios: job.master_ratios,
                            }));
                            setShowMasterModal(false);
                            setRunningMaster(false);
                            finished = true;
                            return;
                        }
                        if (job.status === "failed" || job.status === "crashed") {
                            setError(job.error || "Error al generar el informe master.");
                            setRunningMaster(false);
                            finished = true;
                            return;
                        }
                    } catch (pollErr) {
                        console.warn("polling master job:", pollErr);
                    }
                }
                // Timeout de espera: recargamos la página — si el master está listo, aparecerá.
                window.location.reload();
                return;
            }
        } catch (e) {
            setError(formatApiError(e, "Error al generar Informe Master"));
            setErrorStatus(e?.response?.status || null);
            setRunningMaster(false);
        }
    }

    function shareWhatsApp() {
        const url = window.location.href;
        const msg = `Informe financiero de ${analysis.company_name} (${analysis.period}) - Gestión Fiscal 347: ${url}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    }

    const [publicLink, setPublicLink] = useState(null);
    const [creatingLink, setCreatingLink] = useState(false);

    async function createPublicLink() {
        setCreatingLink(true);
        try {
            const { data } = await api.post(`/analyses/${id}/share`);
            const url = `${window.location.origin}${data.share_url_path}`;
            setPublicLink(url);
            try { await navigator.clipboard.writeText(url); } catch {}
        } catch (e) {
            setError(formatApiError(e, "No se pudo crear el link público"));
        } finally {
            setCreatingLink(false);
        }
    }

    async function revokePublicLink() {
        if (!confirm("¿Revocar el acceso público a este informe?")) return;
        try {
            await api.delete(`/analyses/${id}/share`);
            setPublicLink(null);
        } catch (e) {
            setError(formatApiError(e, "No se pudo revocar"));
        }
    }

    function printPdf() {
        window.print();
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <span className="elegant-loader"><span /><span /><span /></span>
            </div>
        );
    }
    if (!analysis) {
        return (
            <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <p className="text-[var(--gf-danger)] mb-4">{error}</p>
                        <Link to="/dashboard" className="btn-outline">Volver al panel</Link>
                    </div>
                </div>
            </div>
        );
    }

    const ratios = analysis.ratios?.ratios || {};
    const totales = analysis.ratios?.totales || {};
    const balance = analysis.balance || {};
    const interp = analysis.interpretation;

    // Chart data
    const balanceChart = [
        { name: "Activos corrientes", value: balance.activos_corrientes },
        { name: "Activos no corrientes", value: balance.activos_no_corrientes },
        { name: "Pasivos corrientes", value: balance.pasivos_corrientes },
        { name: "Pasivos no corrientes", value: balance.pasivos_no_corrientes },
        { name: "Patrimonio neto", value: balance.patrimonio_neto },
    ];

    const patrimonioPie = [
        { name: "Patrimonio neto", value: balance.patrimonio_neto || 0 },
        { name: "Pasivos totales", value: (balance.pasivos_corrientes || 0) + (balance.pasivos_no_corrientes || 0) },
    ];

    const PIE_COLORS = ["#C5A880", "#0B192C"];

    return (
        <div className="min-h-screen flex flex-col print:bg-white">
            <div className="print:hidden">
                <Navbar />
            </div>

            <main className="flex-1 max-w-6xl mx-auto w-full px-6 md:px-12 py-12">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between gap-6 mb-10 pb-8 border-b border-[var(--gf-border)]">
                    <div>
                        <Link to="/dashboard" className="text-sm text-[var(--gf-text-secondary)] hover:text-[var(--gf-gold)] inline-flex items-center gap-1 mb-3 print:hidden">
                            <ArrowLeft size={14} /> Volver
                        </Link>
                        {/* Logo del asesor (si tiene) — visible en pantalla y en el PDF impreso */}
                        {user?.logo_url && (
                            <img
                                src={user.logo_url}
                                alt="Logo del asesor"
                                className="h-12 md:h-16 max-w-[200px] object-contain mb-4"
                                data-testid="asesor-logo"
                            />
                        )}
                        <div className="micro-label mb-2">— Informe financiero</div>
                        <h1 className="serif text-4xl md:text-5xl">{analysis.company_name}</h1>
                        <div className="mt-2 flex gap-4 text-sm text-[var(--gf-text-secondary)]">
                            <span>Ejercicio <span className="mono font-medium">{analysis.period}</span></span>
                            <span>·</span>
                            <span>{new Date(analysis.created_at).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })}</span>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3 print:hidden">
                        <Link to={`/analisis/${id}/auditoria-bancaria`} className="btn-primary" data-testid="bank-audit-cta">
                            <Bank size={16} weight="light" /> Auditoría bancaria
                        </Link>
                        <button onClick={printPdf} className="btn-outline" data-testid="export-pdf-btn">
                            <Printer size={16} /> Exportar PDF
                        </button>
                        <button onClick={createPublicLink} disabled={creatingLink} className="btn-outline" data-testid="create-public-link-btn">
                            <LinkSimple size={16} /> {creatingLink ? "..." : publicLink ? "Link copiado ✓" : "Link público"}
                        </button>
                        <button onClick={shareWhatsApp} className="btn-gold" data-testid="share-whatsapp-btn" style={{ background: "#25D366" }}>
                            <WhatsappLogo size={16} weight="fill" /> Compartir
                        </button>
                    </div>
                </div>

                {error && <div className="text-sm text-[var(--gf-danger)] mb-6 border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50">{error}</div>}

                {publicLink && (
                    <div className="gf-card mb-6 border-l-4 border-l-[var(--gf-gold)] print:hidden" data-testid="public-link-block">
                        <div className="flex items-start gap-3">
                            <LinkSimple size={22} weight="light" className="text-[var(--gf-gold)] mt-1" />
                            <div className="flex-1">
                                <div className="micro-label mb-1">Link público activo</div>
                                <p className="text-sm text-[var(--gf-text-secondary)] mb-2">
                                    Cualquiera con este link podrá ver este informe en modo lectura (link copiado a tu portapapeles).
                                </p>
                                <div className="font-mono text-xs bg-[var(--gf-subtle)] p-2 border border-[var(--gf-border)] break-all mb-2">{publicLink}</div>
                                <div className="flex gap-3 flex-wrap">
                                    <button onClick={() => navigator.clipboard.writeText(publicLink)} className="text-xs text-[var(--gf-navy)] hover:text-[var(--gf-gold)]">📋 Copiar</button>
                                    <a href={`https://wa.me/?text=${encodeURIComponent(`Informe ${analysis.company_name}: ${publicLink}`)}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--gf-navy)] hover:text-[var(--gf-gold)]">💬 WhatsApp</a>
                                    <button onClick={revokePublicLink} className="text-xs text-[var(--gf-danger)] hover:underline" data-testid="revoke-link-btn">🚫 Revocar</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Aviso descuadre */}
                <DescuadreBanner balance={balance} totales={totales} />

                {/* Totales */}
                <section className="grid md:grid-cols-3 gap-6 mb-6">
                    <TotalCard label="Activos totales" value={totales.activos_totales} />
                    <TotalCard label="Pasivos totales" value={totales.pasivos_totales} />
                    <TotalCard label="Patrimonio neto" value={totales.patrimonio_neto} gold />
                </section>

                {/* Desglose por capítulos */}
                <section className="mb-12" data-testid="balance-breakdown">
                    <div className="micro-label mb-3">— Capítulos del balance</div>
                    <div className="grid md:grid-cols-3 gap-px bg-[var(--gf-border)] border border-[var(--gf-border)]">
                        <BreakdownCol
                            title="Activo"
                            total={totales.activos_totales}
                            items={[
                                { label: "Activo no corriente", value: balance.activos_no_corrientes },
                                { label: "Activo corriente", value: balance.activos_corrientes },
                            ]}
                        />
                        <BreakdownCol
                            title="Pasivo"
                            total={(Number(balance.pasivos_no_corrientes) || 0) + (Number(balance.pasivos_corrientes) || 0)}
                            items={[
                                { label: "Pasivo no corriente", value: balance.pasivos_no_corrientes },
                                { label: "Pasivo corriente", value: balance.pasivos_corrientes },
                            ]}
                        />
                        <BreakdownCol
                            title="Patrimonio Neto"
                            total={totales.patrimonio_neto}
                            items={[
                                { label: "Fondos propios", value: balance.patrimonio_neto },
                            ]}
                            gold
                        />
                    </div>
                </section>

                {/* Ratios */}
                <section className="mb-14">
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <div className="micro-label mb-2">— I · Indicadores</div>
                            <h2 className="serif text-3xl">Ratios financieros</h2>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--gf-border)] border border-[var(--gf-border)]">
                        {Object.entries(ratios).map(([key, r]) => (
                            <RatioCard key={key} label={r.label} value={r.value} health={r.health} optimal={r.optimal} percent={isPct(key)} />
                        ))}
                    </div>
                </section>

                {/* Charts */}
                <section className="mb-14 grid lg:grid-cols-2 gap-6">
                    <div className="gf-card">
                        <div className="micro-label mb-4">— Composición del balance</div>
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={balanceChart} layout="vertical" margin={{ left: 20, right: 20 }}>
                                <CartesianGrid strokeDasharray="2 2" stroke="#E5E0D8" />
                                <XAxis type="number" tickFormatter={(v) => formatEUR(v)} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} stroke="#8B95A5" />
                                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} stroke="#8B95A5" />
                                <Tooltip formatter={(v) => formatEUR(v)} contentStyle={{ background: "#fff", border: "1px solid #E5E0D8", borderRadius: 0, fontFamily: "Manrope" }} />
                                <Bar dataKey="value" fill="#0B192C" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="gf-card">
                        <div className="micro-label mb-4">— Estructura financiera</div>
                        <ResponsiveContainer width="100%" height={320}>
                            <PieChart>
                                <Pie data={patrimonioPie} dataKey="value" innerRadius={60} outerRadius={110} paddingAngle={2}>
                                    {patrimonioPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                                </Pie>
                                <Tooltip formatter={(v) => formatEUR(v)} contentStyle={{ background: "#fff", border: "1px solid #E5E0D8", borderRadius: 0 }} />
                                <Legend iconType="square" />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </section>

                {/* AI Interpretation */}
                <section className="mb-14">
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <div className="micro-label mb-2">— II · Interpretación experta</div>
                            <h2 className="serif text-3xl">Análisis narrativo</h2>
                        </div>
                        {!interp && (
                            <button onClick={() => runInterpretation(false)} disabled={interpreting} className="btn-gold print:hidden" data-testid="generate-interpretation-btn">
                                {interpreting ? (
                                    <>
                                        <span className="elegant-loader"><span /><span /><span /></span>
                                        &nbsp; Analizando...
                                    </>
                                ) : (<><Sparkle size={16} weight="fill" /> Generar con IA</>)}
                            </button>
                        )}
                    </div>

                    {!interp ? (
                        error ? (
                            <div className="gf-card border-l-4 border-l-[var(--gf-danger)] bg-red-50/60" data-testid="no-interpretation-error">
                                <div className="flex items-start gap-4">
                                    <Warning size={32} weight="fill" className="text-[var(--gf-danger)] flex-shrink-0" />
                                    <div className="flex-1">
                                        <h3 className="serif text-xl mb-2">Aviso antes de continuar</h3>
                                        <p className="text-sm text-[var(--gf-text)] mb-4">{error}</p>
                                        <div className="flex flex-wrap gap-3">
                                            {error.toLowerCase().includes("adicional") && error.toLowerCase().includes("confirmar") ? (
                                                <button onClick={() => runInterpretation(true)} disabled={interpreting} className="btn-gold" data-testid="confirm-overage-btn">
                                                    {interpreting ? "Procesando..." : "Confirmar y pagar 8€"} <Sparkle size={14} weight="fill" />
                                                </button>
                                            ) : errorStatus === 402 || errorStatus === 403 ? (
                                                <>
                                                    <QuickRechargeButton amount={24} nextPath={`/analisis/${id}`} testId="quick-recharge-interpret" onError={(m) => setError(m)} />
                                                    <Link to="/pagos" className="btn-outline" data-testid="go-to-payments-btn">
                                                        Otras recargas <Sparkle size={14} weight="fill" />
                                                    </Link>
                                                </>
                                            ) : (
                                                <button onClick={() => runInterpretation(false)} disabled={interpreting} className="btn-gold" data-testid="retry-interpretation-btn">
                                                    {interpreting ? "Reintentando..." : "Reintentar"} <Sparkle size={14} weight="fill" />
                                                </button>
                                            )}
                                            <button onClick={() => { setError(""); setErrorStatus(null); }} className="btn-outline" data-testid="dismiss-error-btn">
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="gf-card text-center py-14" data-testid="no-interpretation">
                                <Sparkle size={40} className="mx-auto text-[var(--gf-gold)] mb-4" weight="light" />
                                <p className="text-[var(--gf-text-secondary)] max-w-md mx-auto">
                                    Genera una interpretación narrativa con IA experta en contabilidad española:
                                    detectamos patrones, alertas fiscales y proponemos consejos accionables.
                                </p>
                            </div>
                        )
                    ) : (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="interpretation-result">
                            {/* Resumen + puntuación */}
                            <div className="gf-card bg-[var(--gf-navy)] text-white border-0 grid md:grid-cols-[1fr_180px] gap-6 items-center">
                                <div>
                                    <div className="micro-label text-[var(--gf-gold)] mb-3">Resumen ejecutivo</div>
                                    <p className="serif text-2xl leading-snug" data-testid="exec-summary">{interp.resumen_ejecutivo}</p>
                                </div>
                                <div className="text-center border-l border-white/20 pl-6">
                                    <div className="micro-label text-white/60 mb-2">Puntuación</div>
                                    <div className="editorial-num text-[var(--gf-gold)]">{interp.puntuacion_global}</div>
                                    <div className="text-xs text-white/60 mt-1">/ 100</div>
                                </div>
                            </div>

                            {/* Interpretación por área */}
                            <div className="grid md:grid-cols-2 gap-6">
                                {interp.interpretacion?.map((bloque, i) => (
                                    <div key={i} className="gf-card-ficha">
                                        <div className="micro-label mb-3">— {String(i + 1).padStart(2, "0")}</div>
                                        <h3 className="serif text-xl mb-3">{bloque.titulo}</h3>
                                        <p className="text-sm leading-relaxed text-[var(--gf-text-secondary)]">{bloque.contenido}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Consejos */}
                            <div>
                                <div className="micro-label mb-3 mt-6">— III · Recomendaciones estratégicas</div>
                                <h3 className="serif text-3xl mb-6 flex items-center gap-3">
                                    <Target size={28} className="text-[var(--gf-gold)]" weight="light" /> Consejos accionables
                                </h3>
                                <div className="space-y-3">
                                    {interp.consejos?.map((c, i) => (
                                        <div key={i} className="gf-card flex gap-4">
                                            <div className="flex-shrink-0">
                                                <Lightbulb size={24} className="text-[var(--gf-gold)]" weight="light" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h4 className="serif text-lg">{c.titulo}</h4>
                                                    <PriorityBadge priority={c.prioridad} />
                                                </div>
                                                <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed">{c.accion}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Alertas fiscales */}
                            {interp.alertas_fiscales?.length > 0 && (
                                <div className="gf-card border-l-4 border-l-[var(--gf-warning)]">
                                    <div className="flex items-start gap-3">
                                        <Warning size={24} className="text-[var(--gf-warning)]" weight="fill" />
                                        <div>
                                            <h3 className="serif text-xl mb-3">Alertas fiscales</h3>
                                            <ul className="space-y-2">
                                                {interp.alertas_fiscales.map((a, i) => (
                                                    <li key={i} className="text-sm flex gap-2">
                                                        <span className="text-[var(--gf-warning)]">—</span>
                                                        <span>{a}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    )}
                </section>

                {/* ============ INFORME MASTER ============ */}
                {interp && (
                    <MasterSection
                        analysis={analysis}
                        onOpen={() => setShowMasterModal(true)}
                        runningMaster={runningMaster}
                    />
                )}

                {/* Pie de impresión — solo visible en PDF · marca emisor + autoría */}
                <div className="hidden print:block mt-12 pt-6 border-t border-[var(--gf-border)] text-[10px] text-[var(--gf-text-muted)] leading-relaxed" data-testid="print-footer">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <div className="font-semibold text-[var(--gf-navy)] mb-1">Emitido por:</div>
                            <div>gf347 · Gestión Fiscal 347</div>
                        </div>
                        <div className="text-right">
                            <div className="font-semibold text-[var(--gf-navy)] mb-1">Plataforma:</div>
                            <div>Gestión Fiscal 347 · gf347.es</div>
                            <div>Generado el {new Date().toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })}</div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Modal Master */}
            {showMasterModal && (
                <MasterModal
                    data={masterData}
                    setData={setMasterData}
                    onClose={() => setShowMasterModal(false)}
                    onGenerate={runMasterReport}
                    loading={runningMaster}
                    error={error}
                    errorStatus={errorStatus}
                    analysisId={id}
                />
            )}

            <div className="print:hidden">
                <Footer />
                <WhatsAppFab />
            </div>
        </div>
    );
}

function isPct(key) {
    return ["roe", "roa", "margen_neto", "endeudamiento", "autonomia_financiera"].includes(key);
}

function TotalCard({ label, value, gold }) {
    return (
        <div className={`gf-card-ficha ${gold ? "border-t-2 border-t-[var(--gf-gold)]" : ""}`} data-testid={`total-${label.replace(/\s/g, "-").toLowerCase()}`}>
            <div className="micro-label mb-3">{label}</div>
            <div className="editorial-num mono">{formatEUR(value || 0)}</div>
        </div>
    );
}

function BreakdownCol({ title, total, items, gold }) {
    const testId = `breakdown-${title.replace(/\s/g, "-").toLowerCase()}`;
    return (
        <div className={`bg-white p-5 ${gold ? "border-t-2 border-t-[var(--gf-gold)]" : ""}`} data-testid={testId}>
            <div className={`micro-label mb-3 ${gold ? "text-[var(--gf-gold)]" : "text-[var(--gf-navy)]"}`}>{title}</div>
            <div className="space-y-2">
                {items.map((it) => (
                    <div key={it.label} className="flex justify-between text-sm">
                        <span className="text-[var(--gf-text-secondary)]">{it.label}</span>
                        <span className="mono">{formatEUR(Number(it.value) || 0)}</span>
                    </div>
                ))}
            </div>
            <div className="mt-4 pt-3 border-t border-[var(--gf-border)] flex justify-between items-baseline">
                <span className="micro-label">Total</span>
                <span className={`mono serif text-xl ${gold ? "text-[var(--gf-gold)]" : "text-[var(--gf-navy)]"}`}>
                    {formatEUR(Number(total) || 0)}
                </span>
            </div>
        </div>
    );
}

function RatioCard({ label, value, health, optimal, percent }) {
    const healthColor = {
        bueno: "var(--gf-success)",
        alerta: "var(--gf-warning)",
        "n/a": "var(--gf-text-muted)",
    }[health] || "var(--gf-text-muted)";
    const healthIcon = {
        bueno: <CheckCircle size={14} weight="fill" />,
        alerta: <Warning size={14} weight="fill" />,
        "n/a": <Info size={14} />,
    }[health];
    const display = value === null || value === undefined
        ? "—"
        : percent
            ? `${formatNum(value * 100, 1)}%`
            : formatNum(value, 2);
    return (
        <div className="bg-white p-5" data-testid={`ratio-${label.replace(/\s/g, "-").toLowerCase()}`}>
            <div className="text-xs text-[var(--gf-text-secondary)] mb-2 h-10">{label}</div>
            <div className="editorial-num mono text-[2.5rem]" style={{ color: "var(--gf-navy)" }}>{display}</div>
            <div className="flex items-center justify-between mt-3 text-xs">
                <div className="text-[var(--gf-text-muted)]">Óptimo: <span className="mono">{optimal}</span></div>
                <div className="flex items-center gap-1" style={{ color: healthColor }}>
                    {healthIcon}
                    <span className="uppercase tracking-wider font-medium">
                        {health === "bueno" ? "Bueno" : health === "alerta" ? "Atención" : "N/A"}
                    </span>
                </div>
            </div>
        </div>
    );
}

function PriorityBadge({ priority }) {
    const map = {
        alta: { bg: "var(--gf-danger)", label: "Alta" },
        media: { bg: "var(--gf-warning)", label: "Media" },
        baja: { bg: "var(--gf-success)", label: "Baja" },
    };
    const p = map[priority] || map.media;
    return (
        <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 text-white" style={{ background: p.bg }}>
            {p.label}
        </span>
    );
}


function MasterSection({ analysis, onOpen, runningMaster }) {
    const mr = analysis.master_report;
    const ratios = analysis.master_ratios;

    if (!mr) {
        return (
            <section className="mb-14 print:hidden" data-testid="master-empty">
                <div className="bg-[var(--gf-navy)] text-white p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-px bg-[var(--gf-gold)]" />
                            <span className="micro-label text-[var(--gf-gold)]">— III · Informe MASTER</span>
                        </div>
                        <h2 className="serif text-3xl md:text-4xl mb-3">
                            El <span className="italic text-[var(--gf-gold)]">análisis completo</span> que tu empresa merece
                        </h2>
                        <p className="text-white/75 leading-relaxed max-w-2xl">
                            EBITDA · Margen operativo · Cobertura de intereses · Rotación de activos e inventarios ·
                            Periodo medio de cobro y pago · Fortalezas · Debilidades · Plan de acción trimestral · 3 escenarios.
                        </p>
                    </div>
                    <button onClick={onOpen} disabled={runningMaster} className="btn-gold whitespace-nowrap" data-testid="open-master-modal-btn">
                        <Sparkle size={16} weight="fill" /> Generar Informe Master
                    </button>
                </div>
            </section>
        );
    }

    const pa = mr.puntuacion_por_area || {};

    return (
        <section className="mb-14" data-testid="master-section">
            <div className="bg-[var(--gf-navy)] text-white p-8 mb-8">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-px bg-[var(--gf-gold)] mt-3" />
                    <span className="micro-label text-[var(--gf-gold)]">— III · INFORME MASTER</span>
                </div>
                <div className="grid md:grid-cols-[1fr_220px] gap-6 items-center">
                    <div>
                        <div className="micro-label text-[var(--gf-gold)] mb-3">Resumen ejecutivo</div>
                        <p className="serif text-2xl leading-snug" data-testid="master-summary">{mr.resumen_ejecutivo}</p>
                    </div>
                    <div className="text-center border-l border-white/20 pl-6">
                        <div className="micro-label text-white/60 mb-2">Global</div>
                        <div className="editorial-num text-[var(--gf-gold)]">{mr.puntuacion_global}</div>
                        <div className="text-xs text-white/60 mt-1">/ 100</div>
                    </div>
                </div>
            </div>

            {/* Puntuaciones por área */}
            {pa && Object.keys(pa).length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[var(--gf-border)] border border-[var(--gf-border)] mb-8">
                    {Object.entries(pa).map(([k, v]) => (
                        <div key={k} className="bg-white p-5">
                            <div className="micro-label mb-2 capitalize">{k.replace(/_/g, " ")}</div>
                            <div className="editorial-num mono" style={{ fontSize: "2.5rem" }}>{v}</div>
                            <div className="h-1 bg-[var(--gf-subtle)] mt-3">
                                <div className="h-1 bg-[var(--gf-gold)]" style={{ width: `${v}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Ratios avanzados */}
            {ratios && (
                <div className="gf-card mb-8">
                    <div className="micro-label mb-4">— Ratios avanzados</div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {Object.entries(ratios).map(([k, r]) => (
                            <div key={k} className="border-t-2 border-t-[var(--gf-navy)] pt-3">
                                <div className="text-xs text-[var(--gf-text-secondary)] mb-1">{r.label}</div>
                                <div className="editorial-num mono" style={{ fontSize: "2rem" }}>
                                    {r.value === null || r.value === undefined
                                        ? "—"
                                        : r.unit === "eur" ? formatEUR(r.value)
                                        : r.unit === "pct" ? `${formatNum(r.value * 100, 1)}%`
                                        : r.unit === "dias" ? `${formatNum(r.value, 0)} d`
                                        : `${formatNum(r.value, 2)}${r.unit === "x" ? "×" : ""}`}
                                </div>
                                {r.optimal && <div className="text-xs text-[var(--gf-text-muted)] mt-1">Óptimo: <span className="mono">{r.optimal}</span></div>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Diagnóstico por categoría */}
            {mr.analisis_por_categoria?.length > 0 && (
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                    {mr.analisis_por_categoria.map((c, i) => (
                        <div key={i} className="gf-card-ficha">
                            <div className="micro-label mb-3">— {String(i + 1).padStart(2, "0")}</div>
                            <h3 className="serif text-xl mb-3">{c.categoria}</h3>
                            <p className="text-sm leading-relaxed text-[var(--gf-text-secondary)] mb-4">{c.diagnostico}</p>
                            {c.ratios_clave?.length > 0 && (
                                <div className="border-t border-[var(--gf-border)] pt-3">
                                    {c.ratios_clave.map((r, j) => (
                                        <div key={j} className="text-xs mono text-[var(--gf-text-secondary)] mb-1">· {r}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Fortalezas / Debilidades / Riesgos */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
                {mr.fortalezas?.length > 0 && (
                    <div className="gf-card border-l-4 border-l-[var(--gf-success)]">
                        <h3 className="serif text-xl mb-4 flex items-center gap-2"><CheckCircle size={20} weight="fill" className="text-[var(--gf-success)]" /> Fortalezas</h3>
                        <ul className="space-y-2 text-sm">
                            {mr.fortalezas.map((f, i) => <li key={i} className="flex gap-2"><span className="text-[var(--gf-success)]">+</span><span>{f}</span></li>)}
                        </ul>
                    </div>
                )}
                {mr.debilidades?.length > 0 && (
                    <div className="gf-card border-l-4 border-l-[var(--gf-warning)]">
                        <h3 className="serif text-xl mb-4 flex items-center gap-2"><Warning size={20} weight="fill" className="text-[var(--gf-warning)]" /> Debilidades</h3>
                        <ul className="space-y-2 text-sm">
                            {mr.debilidades.map((d, i) => <li key={i} className="flex gap-2"><span className="text-[var(--gf-warning)]">−</span><span>{d}</span></li>)}
                        </ul>
                    </div>
                )}
                {mr.riesgos?.length > 0 && (
                    <div className="gf-card border-l-4 border-l-[var(--gf-danger)]">
                        <h3 className="serif text-xl mb-4 flex items-center gap-2"><Warning size={20} weight="fill" className="text-[var(--gf-danger)]" /> Riesgos</h3>
                        <ul className="space-y-3 text-sm">
                            {mr.riesgos.map((r, i) => (
                                <li key={i}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-semibold">{r.titulo}</span>
                                        <PriorityBadge priority={r.gravedad} />
                                    </div>
                                    <div className="text-xs text-[var(--gf-text-secondary)]">{r.descripcion}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Plan de acción + escenarios */}
            {mr.plan_accion?.length > 0 && (
                <div className="gf-card mb-8">
                    <div className="micro-label mb-3">— Plan de acción trimestral</div>
                    <div className="grid md:grid-cols-4 gap-6">
                        {mr.plan_accion.map((q, i) => (
                            <div key={i} className="border-t-2 border-t-[var(--gf-gold)] pt-3">
                                <div className="serif text-xl mb-3">{q.trimestre}</div>
                                <ul className="space-y-2 text-sm">
                                    {q.acciones.map((a, j) => <li key={j} className="flex gap-2"><span className="text-[var(--gf-gold)]">·</span><span>{a}</span></li>)}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {mr.escenarios && (
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <ScenarioCard label="Escenario favorable" text={mr.escenarios.mejora} color="var(--gf-success)" />
                    <ScenarioCard label="Escenario estable" text={mr.escenarios.estancamiento} color="var(--gf-text-secondary)" />
                    <ScenarioCard label="Escenario adverso" text={mr.escenarios.riesgo} color="var(--gf-danger)" />
                </div>
            )}

            {mr.alertas_fiscales?.length > 0 && (
                <div className="gf-card border-l-4 border-l-[var(--gf-warning)]">
                    <h3 className="serif text-xl mb-3">Alertas fiscales avanzadas</h3>
                    <ul className="space-y-2">
                        {mr.alertas_fiscales.map((a, i) => <li key={i} className="text-sm flex gap-2"><span className="text-[var(--gf-warning)]">—</span><span>{a}</span></li>)}
                    </ul>
                </div>
            )}
        </section>
    );
}

function ScenarioCard({ label, text, color }) {
    return (
        <div className="gf-card" style={{ borderTop: `2px solid ${color}` }}>
            <div className="micro-label mb-3" style={{ color }}>{label}</div>
            <p className="text-sm leading-relaxed">{text}</p>
        </div>
    );
}

function MasterModal({ data, setData, onClose, onGenerate, loading, error, errorStatus, analysisId }) {
    const fields = [
        { k: "ebit", label: "EBIT (Resultado antes intereses e impuestos)", required: true },
        { k: "amortizaciones", label: "Amortizaciones" },
        { k: "depreciaciones", label: "Depreciaciones" },
        { k: "gastos_financieros", label: "Gastos financieros" },
        { k: "costo_ventas", label: "Coste de ventas" },
        { k: "clientes", label: "Saldo clientes (deudores)" },
        { k: "proveedores", label: "Saldo proveedores (acreedores)" },
    ];
    return (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-6 overflow-y-auto" data-testid="master-modal">
            <div className="bg-white max-w-2xl w-full my-8 border border-[var(--gf-border)]">
                <div className="p-6 border-b border-[var(--gf-border)] flex items-center justify-between">
                    <div>
                        <div className="micro-label mb-1">— Datos adicionales</div>
                        <h2 className="serif text-2xl">Generar Informe MASTER</h2>
                    </div>
                    <button onClick={onClose} className="text-[var(--gf-text-muted)] hover:text-[var(--gf-navy)]" data-testid="master-modal-close">✕</button>
                </div>
                <div className="p-6">
                    <p className="text-sm text-[var(--gf-text-secondary)] mb-6">
                        Para calcular EBITDA, cobertura de intereses, rotaciones y periodos medios necesitamos estos datos adicionales.
                        Si no los tienes, déjalos en 0 y omitiremos esos ratios.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                        {fields.map((f) => (
                            <div key={f.k}>
                                <label className="gf-label">{f.label} (€)</label>
                                <input
                                    type="number" step="0.01"
                                    className="gf-input mono"
                                    value={data[f.k]}
                                    onChange={(e) => setData({ ...data, [f.k]: e.target.value })}
                                    placeholder="0.00"
                                    data-testid={`master-field-${f.k}`}
                                />
                            </div>
                        ))}
                    </div>
                    {error && (
                        <div className="text-sm text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50 mb-4" data-testid="master-modal-error">
                            {error}
                            {errorStatus === 402 && (
                                <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="master-recharge-block">
                                    <QuickRechargeButton amount={44} nextPath={`/analisis/${analysisId}`} className="btn-gold text-xs" testId="quick-recharge-master" />
                                    <Link to="/pagos" className="text-xs text-[var(--gf-navy)] underline underline-offset-2" data-testid="master-other-recharges">Otras recargas</Link>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="flex flex-wrap gap-3 justify-end pt-4 border-t border-[var(--gf-border)]">
                        <button onClick={onClose} className="btn-outline" data-testid="master-cancel">Cancelar</button>
                        <button onClick={onGenerate} disabled={loading} className="btn-gold" data-testid="master-generate">
                            {loading ? (
                                <>
                                    <span className="elegant-loader"><span /><span /><span /></span>
                                    &nbsp; Elaborando MASTER...
                                </>
                            ) : (<><Sparkle size={16} weight="fill" /> Generar (1 crédito)</>)}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


function DescuadreBanner({ balance, totales }) {
    if (!balance || !totales) return null;
    const activo = Number(totales.activos_totales) || 0;
    // Pasivo + Patrimonio (fondos propios ya incluye resultado)
    const pasivoPat =
        (Number(balance.pasivos_corrientes) || 0) +
        (Number(balance.pasivos_no_corrientes) || 0) +
        (Number(balance.patrimonio_neto) || 0);
    const diferencia = activo - pasivoPat;
    if (Math.abs(diferencia) <= 1) return null;
    const diffStr = new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
    }).format(diferencia);
    const activoStr = formatEUR(activo);
    const pasivoStr = formatEUR(pasivoPat);
    return (
        <div
            className="mb-8 p-5 border-l-4 border-l-red-500 bg-red-50 flex items-start gap-4"
            data-testid="descuadre-banner"
        >
            <Warning size={26} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
                <div className="font-semibold text-red-800 mb-1">
                    Balance descuadrado · Diferencia {diffStr}
                </div>
                <div className="text-sm text-red-700 leading-relaxed">
                    El total del Activo ({activoStr}) no coincide con la suma de Fondos Propios + Pasivo No Corriente + Pasivo Corriente ({pasivoStr}).
                    Los ratios y el diagnóstico pueden estar distorsionados. Revisa las cifras introducidas antes de tomar decisiones basadas en este informe.
                </div>
            </div>
        </div>
    );
}
